import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Upload, ExternalLink, CreditCard, Bell, Save, Camera, HelpCircle, Scale, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { trackSubscription } from "../lib/analytics";
import { useAuth, applyBranding } from "../lib/auth";
import ColorPicker from "../components/ColorPicker";
import BrandPreview from "../components/BrandPreview";
import ThemeToggle from "../components/ThemeToggle";
import PickPlanModal from "../components/PickPlanModal";
import { restartAgentTour } from "../components/WelcomeTour";
import BrokerDisclosure from "../components/BrokerDisclosure";
import { US_STATES } from "../lib/usStates";

// Module constant — presets never change and lifting them out of the JSX
// eliminates a new-array-on-every-render that React would otherwise diff.
const ACCENT_PRESETS = ["#F59E0B", "#EF4444", "#8B5CF6", "#10B981", "#06B6D4", "#F97316", "#EC4899", "#0F172A"];

/**
 * Settings — the single source of truth for profile, branding, billing and
 * notification controls.
 *
 * Contract: ONE "Save changes" button at the bottom of the page (outside every
 * preview/card) commits every editable field in one round trip. Uploads and
 * one-time actions (photo/logo upload, checkout, nudge run) still commit
 * immediately because there's nothing to "save" for them.
 */
export default function Settings() {
  const { agent, refresh, patchAgent } = useAuth();
  const [params] = useSearchParams();

  const [form, setForm] = useState({
    full_name: "", phone: "",
    brand_color: "#1F6FEB", accent_color: "#F59E0B",
    email_signature: "", timezone_name: "America/Phoenix",
    brokerage_name: "",
    mls_search_url: "",
    state: "",
    // Broker disclosure compliance
    title: "",
    license_number: "",
    license_state: "",
    public_email: "",
    brokerage_legal_name: "",
    brokerage_dba: "",
    brokerage_license_number: "",
    office_street: "",
    office_suite: "",
    office_city: "",
    office_state: "",
    office_zip: "",
    office_phone: "",
    independently_owned: false,
    custom_disclaimer: "",
    equal_housing_notice: true,
  });
  const [initial, setInitial] = useState(null);
  const [billing, setBilling] = useState(null);
  const [brokerage, setBrokerage] = useState(null); // { id, name, role, invite_code } | null
  const [disclosurePreview, setDisclosurePreview] = useState(null);
  const [missingRequired, setMissingRequired] = useState([]);
  const [busy, setBusy] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);

  // Rebuild the disclosure preview whenever ANY compliance field changes,
  // so the "at least as prominently as the agent" layout is verifiable in
  // real time without saving. Server-side rendering is authoritative but
  // an optimistic client render keeps the preview responsive.
  useEffect(() => {
    const licState = (form.license_state || form.state || "").toUpperCase() || null;
    setDisclosurePreview({
      agent: {
        full_name: form.full_name || null,
        title: form.title || null,
        license_number: form.license_number || null,
        license_state: licState,
        phone: form.phone || null,
        email: form.public_email || agent?.email || null,
      },
      brokerage: {
        legal_name: form.brokerage_legal_name || null,
        dba: form.brokerage_dba || null,
        license_number: form.brokerage_license_number || null,
        street: form.office_street || null,
        suite: form.office_suite || null,
        city: form.office_city || null,
        state: (form.office_state || "").toUpperCase() || null,
        zip: form.office_zip || null,
        phone: form.office_phone || null,
      },
      disclaimers: {
        independently_owned: !!form.independently_owned,
        equal_housing_notice: !!form.equal_housing_notice,
        custom: form.custom_disclaimer || null,
        state_required: null,
      },
      rules: {},
    });
  }, [form, agent]);

  // Pull the server-side missing-required list. The invite guard uses the
  // same list on the API side, so the UI can't disagree with what the
  // server would 400 on.
  useEffect(() => {
    if (!agent) return;
    api.get("/disclosure/me")
      .then(r => setMissingRequired(r.data.missing_required || []))
      .catch(() => setMissingRequired([]));
  }, [agent, initial]);

  useEffect(() => {
    if (!agent) return;
    // Pull the brokerage first so we can seed brokerage_name from the truth
    // rather than a stale `agent.brokerage_name` mirror. `/brokerage/me`
    // returns null for solo agents.
    const buildForm = (bk) => ({
      full_name: agent.full_name || "", phone: agent.phone || "",
      brand_color: agent.brand_color || "#1F6FEB",
      accent_color: agent.accent_color || "#F59E0B",
      email_signature: agent.email_signature || "",
      timezone_name: agent.timezone_name || "America/Phoenix",
      brokerage_name: (bk && bk.name) || agent.brokerage_name || "",
      mls_search_url: agent.mls_search_url || "",
      state: agent.state || "",
      title: agent.title || "",
      license_number: agent.license_number || "",
      license_state: agent.license_state || "",
      public_email: agent.public_email || "",
      brokerage_legal_name: agent.brokerage_legal_name || "",
      brokerage_dba: agent.brokerage_dba || "",
      brokerage_license_number: agent.brokerage_license_number || "",
      office_street: agent.office_street || "",
      office_suite: agent.office_suite || "",
      office_city: agent.office_city || "",
      office_state: agent.office_state || "",
      office_zip: agent.office_zip || "",
      office_phone: agent.office_phone || "",
      independently_owned: !!agent.independently_owned,
      custom_disclaimer: agent.custom_disclaimer || "",
      equal_housing_notice: agent.equal_housing_notice !== false,
    });
    api.get("/brokerage/me").then(r => {
      setBrokerage(r.data);
      const next = buildForm(r.data);
      setForm(next); setInitial(next);
    }).catch(() => {
      const next = buildForm(null);
      setForm(next); setInitial(next);
    });
    api.get("/billing/me").then(r => setBilling(r.data)).catch((e) => {
      console.debug("billing/me lookup failed", e?.response?.status || e?.message);
    });
  }, [agent]);

  // Post-checkout polling
  useEffect(() => {
    const status = params.get("checkout");
    const sid = params.get("session_id");
    if (status === "success" && sid) {
      const poll = setInterval(async () => {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (data.payment_status === "paid") {
          clearInterval(poll);
          toast.success("Subscription active");
          // Fetch the freshly-flipped billing/me so the GA4 purchase
          // event carries the actual tier + price the agent just paid
          // for. Fires once — trackSubscription dedupes by session_id.
          api.get("/billing/me").then(r => {
            setBilling(r.data);
            const plan = r.data?.plan;
            trackSubscription({
              sessionId: sid,
              tier: plan?.tier,
              tierName: plan?.tier_name,
              value: plan?.price_usd,
            });
          });
        }
      }, 2000);
      setTimeout(() => clearInterval(poll), 30000);
    }
  }, [params]);

  const dirty = useMemo(() => {
    if (!initial) return false;
    return Object.keys(initial).some(k => (form[k] ?? "") !== (initial[k] ?? ""));
  }, [form, initial]);

  const canRenameBrokerage = brokerage?.role === "broker";

  const save = async () => {
    if (!dirty) return;
    setBusy(true);
    try {
      const { brokerage_name, ...settingsBody } = form;
      await api.patch("/settings", settingsBody);

      // Brokerage rename — only when we're the broker and the name changed.
      // Solo agents that type a brokerage name don't create a brokerage row —
      // that's a separate onboarding decision, not a "save".
      if (
        canRenameBrokerage
        && brokerage
        && brokerage_name.trim()
        && brokerage_name.trim() !== brokerage.name
      ) {
        try {
          const { data } = await api.patch("/brokerage", { name: brokerage_name.trim() });
          setBrokerage(b => ({ ...b, name: data.name }));
        } catch (err) {
          toast.warning(err?.response?.data?.detail || "Couldn't rename brokerage");
        }
      }

      applyBranding(form.brand_color, form.accent_color);
      toast.success("Saved");
      setInitial(form);
      refresh();
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const uploadLogo = async (file) => {
    setLogoUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/settings/logo", fd);
      if (data?.logo_url) patchAgent({ logo_url: data.logo_url });
      toast.success("Logo uploaded");
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Upload failed";
      console.error("logo upload failed", e?.response?.status, detail);
      toast.error(detail);
    } finally { setLogoUploading(false); }
  };

  const uploadPhoto = async (file) => {
    setPhotoUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/settings/photo", fd);
      if (data?.photo_url) patchAgent({ photo_url: data.photo_url });
      toast.success("Photo uploaded — clients will see this on their portal");
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Photo upload failed";
      console.error("photo upload failed", e?.response?.status, detail);
      toast.error(detail);
    } finally { setPhotoUploading(false); }
  };

  const [pickPlan, setPickPlan] = useState(false);

  const checkout = () => setPickPlan(true);
  const portal = async () => {
    const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
    window.location.href = data.url;
  };
  const cancelSubscription = async () => {
    if (!window.confirm("Cancel your subscription? You'll keep full access until the end of your current paid period.")) return;
    try {
      const { data } = await api.post("/billing/cancel");
      const endStr = data.current_period_end ? new Date(data.current_period_end).toLocaleDateString() : "the end of your billing cycle";
      toast.success(`Cancellation scheduled. Access remains active through ${endStr}.`);
      api.get("/billing/me").then(r => setBilling(r.data));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };
  const resumeSubscription = async () => {
    try {
      await api.post("/billing/resume");
      toast.success("Subscription resumed");
      api.get("/billing/me").then(r => setBilling(r.data));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resume failed");
    }
  };
  const runNudges = async () => {
    try {
      const { data } = await api.post("/nudges/deadlines/run");
      toast.success(`Sent ${data.notifications_sent} nudge(s) across ${data.devices} device(s)`);
    } catch { toast.error("Nudge run failed"); }
  };

  const inputCls = "mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus-brand";
  const labelCls = "text-xs font-medium text-slate-600 uppercase tracking-wider";

  return (
    <div className="max-w-3xl">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tighter text-slate-900">Profile & Settings</h1>
        <p className="mt-1 text-sm text-slate-500">Your profile, brand, billing, and notification controls — save everything at once at the bottom of the page.</p>
      </div>

      {/* Profile & Branding — the single unified section for every personal
          field. Preview card at the bottom updates in real-time as any field
          changes. */}
      <section className="mt-8 rounded-lg border border-slate-200 bg-white p-6" data-testid="branding-form">
        <h2 className="font-heading font-bold text-lg tracking-tight text-slate-900">Profile & branding</h2>
        <p className="text-xs text-slate-500 mt-0.5">Everything below is applied at runtime to your agent app and every client portal.</p>

        <div className="mt-4 flex items-center justify-between gap-4 p-3 rounded-md bg-slate-50 border border-slate-200">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900">Appearance</div>
            <p className="text-xs text-slate-500 mt-0.5">Dark mode saves your eyes on late-night deal reviews.</p>
          </div>
          <ThemeToggle testIdPrefix="settings-theme" />
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Full name</label>
            <input required value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} className={inputCls} data-testid="settings-name-input" />
          </div>
          <div>
            <label className={labelCls}>Phone</label>
            <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="settings-phone-input" />
          </div>

          <div>
            <label className={labelCls}>State</label>
            <input
              value={form.state}
              onChange={e => setForm({ ...form, state: e.target.value.toUpperCase().slice(0, 2) })}
              placeholder="e.g. CA"
              maxLength={2}
              className={inputCls + " uppercase"}
              data-testid="settings-state-input"
            />
            <p className="mt-1 text-xs text-slate-500">Two-letter code · used to prefill new listings and localise compliance copy.</p>
          </div>

          <div className="col-span-2">
            <label className={labelCls}>Brokerage name</label>
            <input
              value={form.brokerage_name}
              onChange={e => setForm({ ...form, brokerage_name: e.target.value })}
              disabled={brokerage && !canRenameBrokerage}
              title={brokerage && !canRenameBrokerage ? "Only your broker can rename the brokerage" : undefined}
              placeholder="e.g. Acme Realty"
              className={inputCls}
              data-testid="settings-brokerage-name"
            />
            <p className="mt-1 text-xs text-slate-500">
              Shown on client portals right under your name.
              {brokerage && !canRenameBrokerage && " · Only your broker can change this."}
            </p>
          </div>

          <div className="col-span-2">
            <label className={labelCls}>MLS search URL</label>
            <input
              value={form.mls_search_url}
              onChange={e => setForm({ ...form, mls_search_url: e.target.value })}
              placeholder="https://www.zillow.com/homes/for_sale/…"
              className={inputCls}
              data-testid="settings-mls-url"
            />
            <p className="mt-1 text-xs text-slate-500">Embedded as an interactive search inside the client portal's Properties tab. Paste your Zillow / Redfin / MLS URL with your default filters already applied.</p>
          </div>

          <div className="col-span-2">
            <ColorPicker value={form.brand_color} onChange={v => setForm({ ...form, brand_color: v })} label="Primary brand color" testIdPrefix="brand-color" />
            <p className="mt-1 text-xs text-slate-500">Buttons, active tabs, and primary calls-to-action across the CRM and the client portal.</p>
          </div>
          <div className="col-span-2">
            <ColorPicker value={form.accent_color} onChange={v => setForm({ ...form, accent_color: v })} label="Accent color" testIdPrefix="accent-color"
              presets={ACCENT_PRESETS} />
            <p className="mt-1 text-xs text-slate-500">Chips, progress bars, hover glows — the secondary highlight that pairs with your primary.</p>
          </div>

          {/* Live client-portal preview — reads form state directly so every
              keystroke, color-pick, and photo upload is visible without a
              save. */}
          <div className="col-span-2">
            <BrandPreview
              brandColor={form.brand_color}
              accentColor={form.accent_color}
              photoUrl={agent?.photo_url || null}
              fullName={form.full_name || agent?.full_name}
              phone={form.phone || agent?.phone}
              email={agent?.email}
              brokerageName={form.brokerage_name}
            />
          </div>

          <div className="col-span-2">
            <label className={labelCls}>Headshot <span className="text-slate-400 normal-case">· shown to clients on their portal</span></label>
            <div className="mt-1 flex items-center gap-4">
              {agent?.photo_url ? (
                <img src={agent.photo_url} alt="" className="h-14 w-14 rounded-full border border-slate-200 object-cover" data-testid="settings-photo-preview" />
              ) : (
                <div className="h-14 w-14 rounded-full border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-slate-400"><Camera className="h-5 w-5" /></div>
              )}
              <label className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 cursor-pointer">
                <Upload className="h-4 w-4" /> {photoUploading ? "Uploading…" : "Upload headshot"}
                <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={e => e.target.files?.[0] && uploadPhoto(e.target.files[0])} data-testid="settings-photo-input" />
              </label>
            </div>
            <p className="mt-2 text-xs text-slate-500">JPEG, PNG, WebP or GIF · up to 10MB. iPhone HEIC photos won't display in browsers — export as JPEG first.</p>
          </div>

          <div className="col-span-2">
            <label className={labelCls}>Brokerage logo</label>
            <div className="mt-1 flex items-center gap-4">
              {agent?.logo_url && <img src={agent.logo_url} alt="" className="h-10 border border-slate-200 rounded p-1 bg-white" />}
              <label className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 cursor-pointer">
                <Upload className="h-4 w-4" /> {logoUploading ? "Uploading…" : "Upload logo"}
                <input type="file" accept="image/jpeg,image/png,image/webp,image/svg+xml,image/gif" className="hidden" onChange={e => e.target.files?.[0] && uploadLogo(e.target.files[0])} data-testid="settings-logo-input" />
              </label>
            </div>
          </div>

          <div className="col-span-2">
            <label className={labelCls}>Email signature</label>
            <textarea value={form.email_signature} onChange={e => setForm({ ...form, email_signature: e.target.value })} rows={3} className={inputCls} />
          </div>
        </div>
      </section>

      {/* Brokerage & licensing — nationwide advertising-compliance section.
          State real-estate rules require the employing brokerage to be
          identified clearly on every consumer-facing surface. Missing any
          of the required fields (starred) blocks portal invites at the API
          layer, so the top-of-section banner is the same signal the server
          would give back on the 400. */}
      <section
        id="brokerage-licensing"
        className="mt-6 rounded-lg border border-slate-200 bg-white p-6"
        data-testid="brokerage-licensing-form"
      >
        <div className="flex items-start gap-3">
          <Scale className="h-5 w-5 text-slate-500 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <h2 className="font-heading font-bold text-lg tracking-tight text-slate-900">Brokerage &amp; licensing</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Required for state advertising compliance. These fields render on the client portal,
              portal invitation emails, and any consumer-facing share page. You and your designated
              broker are responsible for the accuracy of what you enter here — iKEY does not certify legal compliance.
            </p>
          </div>
        </div>

        {missingRequired.length > 0 && (
          <div
            className="mt-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-[13px] text-amber-900"
            data-testid="disclosure-missing-banner"
          >
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="font-semibold">Portal invites are blocked until you complete these fields.</p>
              <p className="mt-0.5 opacity-90">
                Most state real-estate commissions treat portal invitations as advertising and require the employing broker to be named. Complete every starred field below to unlock the "Send portal invite" button.
              </p>
            </div>
          </div>
        )}

        {/* Agent identity */}
        <p className="mt-6 text-[10px] font-semibold uppercase tracking-widest text-slate-500">Agent identity</p>
        <div className="mt-2 grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Display name</label>
            <input value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} className={inputCls} data-testid="settings-agent-display-name" />
          </div>
          <div>
            <label className={labelCls}>Title</label>
            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Realtor®, Associate Broker" className={inputCls} data-testid="settings-agent-title" />
          </div>
          <div>
            <label className={labelCls}>License number <span className="text-rose-600">*</span></label>
            <input value={form.license_number} onChange={e => setForm({ ...form, license_number: e.target.value })} className={inputCls} data-testid="settings-agent-license" />
          </div>
          <div>
            <label className={labelCls}>License state</label>
            <select
              value={form.license_state}
              onChange={e => setForm({ ...form, license_state: e.target.value })}
              className={inputCls}
              data-testid="settings-agent-license-state"
            >
              <option value="">— Select —</option>
              {US_STATES.map(([code, name]) => <option key={code} value={code}>{code} · {name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Direct phone</label>
            <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="settings-agent-direct-phone" />
          </div>
          <div>
            <label className={labelCls}>Public email</label>
            <input value={form.public_email} onChange={e => setForm({ ...form, public_email: e.target.value })} placeholder={agent?.email || "you@brokerage.com"} className={inputCls} data-testid="settings-agent-public-email" />
            <p className="mt-1 text-xs text-slate-500">Optional · leave blank to use your login email on client-facing surfaces.</p>
          </div>
        </div>

        {/* Brokerage identity */}
        <p className="mt-8 text-[10px] font-semibold uppercase tracking-widest text-slate-500">Brokerage identity</p>
        <div className="mt-2 grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className={labelCls}>Legal name (as registered with your state) <span className="text-rose-600">*</span></label>
            <input value={form.brokerage_legal_name} onChange={e => setForm({ ...form, brokerage_legal_name: e.target.value })} className={inputCls} data-testid="settings-brokerage-legal-name" />
          </div>
          <div className="col-span-2">
            <label className={labelCls}>DBA (optional)</label>
            <input value={form.brokerage_dba} onChange={e => setForm({ ...form, brokerage_dba: e.target.value })} className={inputCls} data-testid="settings-brokerage-dba" />
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Brokerage license number <span className="text-rose-600">*</span></label>
            <input value={form.brokerage_license_number} onChange={e => setForm({ ...form, brokerage_license_number: e.target.value })} className={inputCls} data-testid="settings-brokerage-license" />
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Office street address <span className="text-rose-600">*</span></label>
            <input value={form.office_street} onChange={e => setForm({ ...form, office_street: e.target.value })} className={inputCls} data-testid="settings-office-street" />
          </div>
          <div>
            <label className={labelCls}>Suite</label>
            <input value={form.office_suite} onChange={e => setForm({ ...form, office_suite: e.target.value })} className={inputCls} data-testid="settings-office-suite" />
          </div>
          <div>
            <label className={labelCls}>City</label>
            <input value={form.office_city} onChange={e => setForm({ ...form, office_city: e.target.value })} className={inputCls} data-testid="settings-office-city" />
          </div>
          <div>
            <label className={labelCls}>State</label>
            <select value={form.office_state} onChange={e => setForm({ ...form, office_state: e.target.value })} className={inputCls} data-testid="settings-office-state">
              <option value="">— Select —</option>
              {US_STATES.map(([code, name]) => <option key={code} value={code}>{code} · {name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>ZIP</label>
            <input value={form.office_zip} onChange={e => setForm({ ...form, office_zip: e.target.value })} className={inputCls} data-testid="settings-office-zip" />
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Office phone</label>
            <input value={form.office_phone} onChange={e => setForm({ ...form, office_phone: e.target.value })} className={inputCls} data-testid="settings-office-phone" />
          </div>
        </div>

        {/* Disclaimers */}
        <p className="mt-8 text-[10px] font-semibold uppercase tracking-widest text-slate-500">Disclaimers</p>
        <div className="mt-2 space-y-3">
          <label className="flex items-start gap-2 text-sm text-slate-800">
            <input
              type="checkbox"
              checked={!!form.independently_owned}
              onChange={e => setForm({ ...form, independently_owned: e.target.checked })}
              className="mt-1"
              data-testid="settings-independently-owned"
            />
            <span>Each office independently owned and operated <span className="text-slate-500">(required for most franchise brokerages)</span>.</span>
          </label>
          <label className="flex items-start gap-2 text-sm text-slate-800">
            <input
              type="checkbox"
              checked={!!form.equal_housing_notice}
              onChange={e => setForm({ ...form, equal_housing_notice: e.target.checked })}
              className="mt-1"
              data-testid="settings-equal-housing"
            />
            <span>Show the Equal Housing Opportunity notice.</span>
          </label>
          <div>
            <label className={labelCls}>Custom disclaimer</label>
            <textarea
              value={form.custom_disclaimer}
              onChange={e => setForm({ ...form, custom_disclaimer: e.target.value })}
              rows={3}
              placeholder="Any brokerage-specific required language…"
              className={inputCls}
              data-testid="settings-custom-disclaimer"
            />
          </div>
        </div>

        {/* Live preview — mirrors the "Full" component that renders on the
            client portal so the agent can confirm the brokerage line reads
            at the same weight as their own before saving. */}
        <p className="mt-8 text-[10px] font-semibold uppercase tracking-widest text-slate-500">Live preview · client portal</p>
        <div className="mt-2" data-testid="disclosure-preview">
          <BrokerDisclosure disclosure={disclosurePreview} variant="full" />
        </div>
      </section>

      {/* Save bar — sits between Profile & Branding and Billing, outside any
          preview container. One click PATCHes /settings + /brokerage together. */}
      <div className="mt-6 flex items-center justify-end gap-3">
        {dirty && (
          <button
            type="button"
            onClick={() => setForm(initial)}
            className="text-sm text-slate-600 hover:text-slate-900 font-medium px-3 py-2"
            data-testid="settings-discard-btn"
          >
            Discard changes
          </button>
        )}
        <button
          type="button"
          onClick={save}
          disabled={busy || !dirty}
          className="inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold text-white shadow-sm hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: "var(--primary-hex)" }}
          data-testid="settings-save-btn"
        >
          <Save className="h-4 w-4" />
          {busy ? "Saving…" : dirty ? "Save changes" : "All changes saved"}
        </button>
      </div>

      {/* Billing */}
      <section className="mt-6 rounded-lg border border-slate-200 bg-white p-6" data-testid="billing-card">
        <div className="flex items-center gap-3">
          <CreditCard className="h-5 w-5 text-slate-500" />
          <h2 className="font-heading font-bold text-lg tracking-tight text-slate-900">Billing</h2>
        </div>
        <div className="mt-4 flex items-center justify-between gap-6 rounded-md bg-slate-50 border border-slate-200 p-4 flex-wrap">
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wider">Current plan</div>
            <div className="mt-1 flex items-baseline gap-2" data-testid="billing-tier">
              <span className="font-heading font-bold text-lg tracking-tight text-slate-900">
                {billing?.plan?.tier_name || "Free"}
              </span>
              {billing?.plan && billing.plan.price_usd > 0 && (
                <span className="text-xs text-slate-500 tabular-nums">${billing.plan.price_usd.toFixed(2)}/mo</span>
              )}
              {billing?.plan?.exempt && (
                <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">Unlimited · founder</span>
              )}
            </div>
            <div className="mt-1 text-xs text-slate-500" data-testid="billing-usage">
              {billing?.plan?.exempt
                ? `${billing?.plan?.client_count ?? 0} clients · no tier limits apply to this account`
                : billing?.plan?.client_limit === null
                  ? `${billing?.plan?.client_count ?? 0} clients · unlimited`
                  : `${billing?.plan?.client_count ?? 0}/${billing?.plan?.client_limit ?? "—"} clients`}
              {" · "}
              <span className={billing?.plan?.docusign_unlocked ? "text-emerald-700" : "text-slate-500"}>
                DocuSign {billing?.plan?.docusign_unlocked ? "unlocked" : "locked"}
              </span>
            </div>
            <div className="mt-2 text-xs" data-testid="billing-status">
              <span className={billing?.subscription_status === "active" ? "text-emerald-700" : billing?.subscription_status === "canceling" ? "text-amber-700" : "text-slate-500"}>
                {billing?.subscription_status === "active" && "Subscription active"}
                {billing?.subscription_status === "canceling" && "Canceling at period end"}
                {(!billing?.subscription_status || (billing.subscription_status !== "active" && billing.subscription_status !== "canceling")) && "No paid subscription"}
              </span>
              {billing?.current_period_end && (
                <span className="text-slate-500" data-testid="billing-period-end">
                  {" · "}
                  {billing?.subscription_status === "canceling"
                    ? `access through ${new Date(billing.current_period_end).toLocaleDateString()}`
                    : `renews ${new Date(billing.current_period_end).toLocaleDateString()}`}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {billing?.subscription_status !== "active" && billing?.subscription_status !== "canceling" && (
              <button onClick={checkout} className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white" style={{ background: "var(--primary-hex)" }} data-testid="billing-checkout-btn">
                {billing?.plan?.tier === "free" ? "Upgrade plan" : "Pick your plan"}
              </button>
            )}
            {billing?.subscription_status === "active" && (
              <>
                <button onClick={checkout} className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" data-testid="billing-change-plan-btn">
                  Change plan
                </button>
                <button onClick={portal} className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" data-testid="billing-portal-btn">
                  <ExternalLink className="h-4 w-4" /> Manage billing
                </button>
                <button onClick={cancelSubscription} className="inline-flex items-center rounded-md border border-rose-200 bg-white px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50" data-testid="billing-cancel-btn">
                  Cancel subscription
                </button>
              </>
            )}
            {billing?.subscription_status === "canceling" && (
              <button onClick={resumeSubscription} className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white" style={{ background: "var(--primary-hex)" }} data-testid="billing-resume-btn">
                Resume subscription
              </button>
            )}
          </div>
        </div>
        <p className="mt-3 text-xs text-slate-500">Stripe-hosted checkout · handles tax and PCI for you · <b>fair cancellation guarantee</b> — you keep access through the end of your paid period.</p>
      </section>

      {/* Deadline nudges */}
      <section className="mt-6 rounded-lg border border-slate-200 bg-white p-6" data-testid="nudges-card">
        <div className="flex items-center gap-3">
          <Bell className="h-5 w-5 text-slate-500" />
          <h2 className="font-heading font-bold text-lg tracking-tight text-slate-900">Deadline nudges</h2>
        </div>
        <p className="mt-2 text-sm text-slate-600">Push notifications to your mobile app when any contract deadline is inside the next 48 hours. Registered on each mobile sign-in.</p>
        <div className="mt-4 flex items-center justify-end gap-3">
          <button onClick={runNudges} className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" data-testid="nudges-run-btn">
            <Bell className="h-4 w-4" /> Send nudges now
          </button>
        </div>
      </section>

      {/* Guided tour — one-click relaunch of the first-run onboarding. */}
      <section className="mt-6 rounded-lg border border-slate-200 bg-white p-6 mb-8" data-testid="tour-card">
        <div className="flex items-center gap-3">
          <HelpCircle className="h-5 w-5 text-slate-500" />
          <h2 className="font-heading font-bold text-lg tracking-tight text-slate-900">Guided tour</h2>
        </div>
        <p className="mt-2 text-sm text-slate-600">Replay the 60-second walkthrough covering your profile, AI briefings, listings, documents, and client messaging.</p>
        <div className="mt-4 flex items-center justify-end">
          <button
            onClick={() => { restartAgentTour(); toast.success("Starting the tour…"); }}
            className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white hover:opacity-95"
            style={{ background: "var(--primary-hex)" }}
            data-testid="restart-agent-tour-btn"
          >
            <HelpCircle className="h-4 w-4" /> Restart the tour
          </button>
        </div>
      </section>

      {pickPlan && <PickPlanModal onClose={() => setPickPlan(false)} />}
    </div>
  );
}
